import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { offlinePost } from '../lib/offline';
import { ApiError, api, apiUrl } from './client';
import type {
  AccountingPeriod,
  AdminLogins,
  AdminQueues,
  AdminUser,
  ApiKeyInfo,
  AppNotification,
  ApprovalRequest,
  AresContact,
  AutomationMeta,
  AutomationRule,
  AutomationRun,
  BackupRecord,
  BankAccount,
  BankImportResult,
  BusinessSummary,
  Comment,
  Company,
  Contact,
  ContactDetail,
  ControlStatement,
  Dashboard,
  DocType,
  Document,
  DocumentArchiveRecord,
  DocumentSummary,
  EcSalesList,
  EmailRecord,
  EmailTemplateRecord,
  ExchangeRateInfo,
  ExportPackage,
  FeatureInfo,
  FxDifferences,
  ImportAnalysis,
  ImportBatch,
  ImportPreview,
  ImportResult,
  ImportSummary,
  ImportTarget,
  IssueResponse,
  Job,
  JobItemKind,
  JobKind,
  JobPrint,
  JobStatus,
  JobsSummary,
  Journal,
  LoginEventInfo,
  LoginResponse,
  MemberInfo,
  MeResponse,
  MfaStatus,
  NewDocumentInput,
  NumberAllocation,
  NumberSeries,
  OpsPerf,
  OpsStatus,
  Overview,
  PartnerKind,
  PaymentCandidate,
  PaymentListItem,
  PaymentSuggestionGroup,
  Permission,
  PriceList,
  PriceListItem,
  PriceResult,
  Product,
  ProductCategory,
  PurchaseProposal,
  ReportFilters,
  ReportKind,
  ReportOut,
  RetentionPolicy,
  RetentionRun,
  RoleCode,
  SavedView,
  ScanResult,
  SearchResult,
  SessionInfo,
  StockKind,
  StockKindOverview,
  StockMovement,
  StockSummary,
  StockTake,
  StockTrace,
  StockUnit,
  StockUnitDoc,
  StockUnitDocs,
  StockUnitGroup,
  TaxRegistration,
  TelegramLinkCode,
  TelegramPreference,
  TelegramStatus,
  UblCheck,
  User,
  VatOverview,
  VatReturnResponse,
  Warehouse,
  WarehouseLocation,
  WebCustomerOrders,
  WebCustomers,
  WebDimension,
  WebDimRow,
  Webhook,
  WebhookDelivery,
  WebhookEvent,
  WebOrders,
  WebOverview,
  WebRealtime,
  WebSite,
  WidgetKey,
} from './types';

// ---- auth ----

export const meKey = ['me'] as const;

/** Celá odpověď /me: uživatel, organizace s rolemi a oprávněními, feature flagy. */
export function useMeQuery() {
  return useQuery({
    queryKey: meKey,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await api.get<MeResponse>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useMe() {
  const q = useMeQuery();
  return { ...q, data: q.data === undefined ? undefined : (q.data?.user ?? null) };
}

const ALL: Permission[] = [];

/**
 * Oprávnění přihlášeného uživatele ve firmě. Server je vynucuje tak jako tak; tady se
 * podle nich jen schovávají akce, na které by přišla 403. Bez firmy (režim „Všechny
 * firmy") nic nedovolí.
 */
export function useCan(companyId: string | null | undefined) {
  const { data } = useMeQuery();
  const access = companyId
    ? data?.organizations?.flatMap((o) => o.companies).find((c) => c.id === companyId)
    : undefined;
  const permissions = access?.permissions ?? ALL;
  return {
    role: access?.role ?? null,
    permissions,
    can: (key: Permission) => permissions.includes(key),
    /**
     * dokud /me s organizacemi nedorazí, akce se neschovávají (jinak by při načítání blikaly);
     * firma, kterou /me ještě nezná (právě založená), se taky nebere jako „bez oprávnění"
     */
    loaded: data?.organizations !== undefined && (!companyId || !!access),
  };
}

/** Zapnuté feature flagy (výchozí stav všech je vypnuto). */
export function useFeatureFlag(key: string, companyId?: string | null) {
  const { data } = useMeQuery();
  return (data?.featureFlags ?? []).some(
    (f) => f.key === key && (f.companyId === null || f.companyId === companyId),
  );
}

export function useAuthStatus() {
  return useQuery({
    queryKey: ['auth-status'],
    queryFn: () => api.get<{ registrationOpen: boolean }>('/api/auth/status'),
  });
}

export function useLogin(mode: 'login' | 'register') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<LoginResponse>(`/api/auth/${mode}`, input),
    onSuccess: ({ user }) => {
      if (!user) return; // MFA: session vznikne až po druhém kroku
      qc.setQueryData(meKey, { user });
      qc.invalidateQueries({ queryKey: meKey });
      qc.invalidateQueries({ queryKey: ['auth-status'] });
    },
  });
}

/** Změna hesla přihlášeného uživatele; přihlášení zůstává v platnosti. */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<void>('/api/auth/password', input),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      qc.setQueryData(meKey, null);
      qc.clear();
      qc.setQueryData(meKey, null);
    },
  });
}

// ---- sessions a historie přihlášení ----

export const sessionsKey = ['sessions'] as const;

export function useSessions() {
  return useQuery({
    queryKey: sessionsKey,
    queryFn: async () =>
      (await api.get<{ sessions: SessionInfo[] }>('/api/auth/sessions')).sessions,
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ revoked: number }>('/api/auth/sessions'),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useLoginEvents() {
  return useQuery({
    queryKey: ['login-events'],
    queryFn: async () =>
      (await api.get<{ events: LoginEventInfo[] }>('/api/auth/login-events')).events,
  });
}

// ---- členové firmy ----

export const membersKey = (companyId: string) => ['members', companyId] as const;

export function useMembers(companyId: string) {
  return useQuery({
    queryKey: membersKey(companyId),
    queryFn: async () =>
      (await api.get<{ members: MemberInfo[] }>(`/api/companies/${companyId}/members`)).members,
    enabled: !!companyId,
  });
}

export function useAddMember(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: RoleCode }) =>
      api.post<{ member: MemberInfo }>(`/api/companies/${companyId}/members`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: membersKey(companyId) }),
  });
}

export function useUpdateMember(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: { userId: string; role?: RoleCode }) =>
      api.patch<{ member: MemberInfo }>(`/api/companies/${companyId}/members/${userId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: membersKey(companyId) }),
  });
}

export function useRemoveMember(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/companies/${companyId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: membersKey(companyId) }),
  });
}

// ---- API klíče ----

export const apiKeysKey = (companyId: string) => ['api-keys', companyId] as const;

// ---- kategorie zboží ----

export function useCategories(companyId?: string | null) {
  return useQuery({
    queryKey: ['categories', companyId ?? ''],
    queryFn: async () =>
      (
        await api.get<{ categories: ProductCategory[] }>(
          `/api/stock/categories?companyId=${companyId}`,
        )
      ).categories,
    enabled: !!companyId,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; name: string; parentId?: string | null }) =>
      api.post<{ category: ProductCategory }>('/api/stock/categories', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

// ---- ceníky ----

export function usePriceLists(companyId: string) {
  return useQuery({
    queryKey: ['pricelists', companyId],
    queryFn: async () =>
      (await api.get<{ priceLists: PriceList[] }>(`/api/pricelists?companyId=${companyId}`))
        .priceLists,
    enabled: !!companyId,
  });
}

export function useCreatePriceList(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; name: string; isDefault?: boolean }) =>
      api.post<{ priceList: PriceList }>('/api/pricelists', { companyId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricelists', companyId] }),
  });
}

export function useUpdatePriceList(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) =>
      api.patch<{ priceList: PriceList }>(`/api/pricelists/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricelists', companyId] }),
  });
}

export function usePriceListItems(id: string) {
  return useQuery({
    queryKey: ['pricelist-items', id],
    queryFn: async () =>
      (await api.get<{ items: PriceListItem[] }>(`/api/pricelists/${id}/items`)).items,
    enabled: !!id,
  });
}

export function useUpsertPriceListItems(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      items: {
        productId: string;
        variantId?: string | null;
        minQuantity?: string;
        price: string;
      }[];
    }) => api.put<{ items: PriceListItem[] }>(`/api/pricelists/${id}/items`, input),
    onSuccess: ({ items }) => {
      qc.setQueryData(['pricelist-items', id], items);
      qc.invalidateQueries({ queryKey: ['pricelists'] });
    },
  });
}

export function useRemovePriceListItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.delete<{ items: PriceListItem[] }>(`/api/pricelists/${id}/items/${itemId}`),
    onSuccess: ({ items }) => {
      qc.setQueryData(['pricelist-items', id], items);
      qc.invalidateQueries({ queryKey: ['pricelists'] });
    },
  });
}

/** Cena zboží pro partnera (ceník partnera → výchozí → produkt); null = bez ceny. */
export function fetchPrice(params: {
  companyId: string;
  productId: string;
  variantId?: string | null;
  contactId?: string | null;
  quantity?: string;
}): Promise<PriceResult> {
  const qs = new URLSearchParams({ companyId: params.companyId, productId: params.productId });
  if (params.variantId) qs.set('variantId', params.variantId);
  if (params.contactId) qs.set('contactId', params.contactId);
  if (params.quantity) qs.set('quantity', params.quantity);
  return api.get<PriceResult>(`/api/pricelists/price?${qs}`);
}

export function useApiKeys(companyId: string) {
  return useQuery({
    queryKey: apiKeysKey(companyId),
    queryFn: async () =>
      (await api.get<{ keys: ApiKeyInfo[] }>(`/api/companies/${companyId}/api-keys`)).keys,
    enabled: !!companyId,
  });
}

export function useCreateApiKey(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; permissions: Permission[]; ipAllowlist?: string[] }) =>
      api.post<{ key: ApiKeyInfo }>(`/api/companies/${companyId}/api-keys`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey(companyId) }),
  });
}

export function useRevokeApiKey(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.delete(`/api/companies/${companyId}/api-keys/${keyId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey(companyId) }),
  });
}

// ---- firmy ----

export const companiesKey = ['companies'] as const;

export function useCompanies() {
  return useQuery({
    queryKey: companiesKey,
    queryFn: async () => (await api.get<{ companies: Company[] }>('/api/companies')).companies,
    staleTime: 60_000,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ company: Company }>('/api/companies', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companiesKey });
      // oprávnění v nové firmě nese /me – bez obnovy by appka v nové firmě všechno schovala
      qc.invalidateQueries({ queryKey: meKey });
    },
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch<{ company: Company }>(`/api/companies/${id}`, data),
    onSuccess: ({ company }) => {
      qc.setQueryData<Company[]>(companiesKey, (old) =>
        old?.map((s) => (s.id === company.id ? company : s)),
      );
    },
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/companies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKey }),
  });
}

export function useNumberSeries(companyId: string) {
  return useQuery({
    queryKey: ['number-series', companyId],
    queryFn: async () =>
      (await api.get<{ series: NumberSeries[] }>(`/api/companies/${companyId}/number-series`))
        .series,
    enabled: !!companyId,
  });
}

export function useUpdateNumberSeries(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      docType,
      ...data
    }: {
      docType: string;
      prefix: string;
      next: number;
      digits: number;
      pattern?: string | null;
      resetPolicy?: 'never' | 'year';
    }) =>
      api.put<{ series: NumberSeries }>(
        `/api/companies/${companyId}/number-series/${docType}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['number-series', companyId] }),
  });
}

// ---- kontakty ----

/** Adresář jedné firmy; bez `companyId` (režim „Všechny firmy") kontakty všech firem. */
export function useContacts(
  companyId?: string | null,
  q = '',
  kind?: PartnerKind,
  /** documents:desc = nejčastější partneři nahoře */
  sort?: 'documents:desc',
) {
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  if (q) params.set('q', q);
  if (kind) params.set('kind', kind);
  if (sort) params.set('sort', sort);
  return useInfiniteList<'contacts', Contact>(
    ['contacts', companyId ?? 'all', q, kind ?? '', sort ?? ''],
    '/api/contacts',
    params,
    'contacts',
  );
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: async () => (await api.get<{ contact: ContactDetail }>(`/api/contacts/${id}`)).contact,
    enabled: !!id,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ contact: Contact }>('/api/contacts', input),
    onSuccess: ({ contact }) => {
      qc.setQueryData(['contact', contact.id], contact);
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch<{ contact: Contact }>(`/api/contacts/${id}`, data),
    onSuccess: ({ contact }) => {
      qc.setQueryData(['contact', contact.id], contact);
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/** Podzáznamy kontaktu vrací celý detail – uloží se rovnou do cache detailu. */
function useContactSub<TInput>(
  contactId: string,
  method: 'post' | 'delete',
  path: (input: TInput) => string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      method === 'post'
        ? api.post<{ contact: ContactDetail }>(`/api/contacts/${contactId}/${path(input)}`, input)
        : api.delete<{ contact: ContactDetail }>(`/api/contacts/${contactId}/${path(input)}`),
    onSuccess: ({ contact }) => {
      qc.setQueryData(['contact', contactId], contact);
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export const useAddContactAddress = (contactId: string) =>
  useContactSub<Record<string, unknown>>(contactId, 'post', () => 'addresses');
export const useRemoveContactAddress = (contactId: string) =>
  useContactSub<string>(contactId, 'delete', (id) => `addresses/${id}`);
export const useAddContactPerson = (contactId: string) =>
  useContactSub<Record<string, unknown>>(contactId, 'post', () => 'people');
export const useRemoveContactPerson = (contactId: string) =>
  useContactSub<string>(contactId, 'delete', (id) => `people/${id}`);
export const useAddContactBankAccount = (contactId: string) =>
  useContactSub<Record<string, unknown>>(contactId, 'post', () => 'bank-accounts');
export const useRemoveContactBankAccount = (contactId: string) =>
  useContactSub<string>(contactId, 'delete', (id) => `bank-accounts/${id}`);

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/contacts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function aresLookup(ico: string): Promise<AresContact> {
  return api.get<AresContact>(`/api/ares/${ico}`);
}

// ---- Fáze 15: stránkované seznamy (keyset kurzor z API) ----

export const PAGE_SIZE = 50;

/** Odpověď stránkovaného seznamu: položky pod klíčem seznamu + kurzor další stránky. */
export type ListPage<K extends string, T> = { [P in K]: T[] } & {
  total?: number | null;
  /** počty podle stavu (kusy skladu) – jen s první stránkou */
  counts?: Record<string, number> | null;
  nextCursor?: string | null;
  hasMore?: boolean;
};

/**
 * `useInfiniteQuery` nad seznamem API: `data` je sloučené pole všech načtených stránek
 * (stejné použití jako dřív), navíc `hasNextPage`, `fetchNextPage` a `total` z první stránky.
 */
function useInfiniteList<K extends string, T>(
  queryKey: unknown[],
  path: string,
  params: URLSearchParams,
  key: K,
  opts: { enabled?: boolean } = {},
) {
  const base = params.toString();
  const q = useInfiniteQuery({
    queryKey: [...queryKey, 'inf'],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const sp = new URLSearchParams(base);
      sp.set('limit', String(PAGE_SIZE));
      if (pageParam) sp.set('cursor', pageParam);
      return api.get<ListPage<K, T>>(`${path}?${sp.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    // seznamy: 30 s bez znovunačtení při návratu na obrazovku (fáze 15)
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
  const pages = q.data?.pages;
  const data = useMemo(() => pages?.flatMap((p) => p[key] ?? []) ?? [], [pages, key]);
  return {
    data,
    total: pages?.[0]?.total ?? null,
    counts: pages?.[0]?.counts ?? null,
    isPending: q.isPending,
    isFetching: q.isFetching,
    error: q.error,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    fetchNextPage: () => void q.fetchNextPage(),
    refetch: q.refetch,
  };
}

// ---- doklady ----

export interface DocumentsQuery {
  companyId?: string;
  type?: DocType;
  status?: 'open' | 'overdue' | 'paid' | 'draft' | 'cancelled';
  direction?: 'issued' | 'received';
  contactId?: string;
  from?: string;
  to?: string;
  q?: string;
  /** '1' = dobropisy a daňové doklady k platbě se vrátí jako `children` pod svým dokladem */
  group?: '1';
  /** výchozí issuedAt:desc – datum, v rámci dne čísla */
  sort?: DocumentsSort;
  /** 'missing' = jen vydané faktury, ke kterým chybí nákupní doklad */
  purchase?: 'missing';
}

export const DOCUMENT_SORTS = [
  'issuedAt:desc',
  'issuedAt:asc',
  'number:desc',
  'number:asc',
] as const;
export type DocumentsSort = (typeof DOCUMENT_SORTS)[number];

export function useDocuments(query: DocumentsQuery = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
  return useInfiniteList<'documents', DocumentSummary>(
    ['documents', query],
    '/api/documents',
    params,
    'documents',
  );
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: async () => (await api.get<{ document: Document }>(`/api/documents/${id}`)).document,
    enabled: !!id,
  });
}

function rememberDocument(qc: ReturnType<typeof useQueryClient>, document: Document) {
  qc.setQueryData(['document', document.id], document);
  qc.invalidateQueries({ queryKey: ['documents'] });
  qc.invalidateQueries({ queryKey: ['contact', document.contactId] });
  qc.invalidateQueries({ queryKey: ['contacts'] });
  qc.invalidateQueries({ queryKey: ['number-series'] });
  qc.invalidateQueries({ queryKey: companiesKey });
  // vystavení / storno hýbe skladem: prodané kusy nesmí zůstat v nabídce dalšího dokladu
  if ((document.items ?? []).some((i) => i.productId)) {
    qc.invalidateQueries({ queryKey: ['stock-units'] });
    qc.invalidateQueries({ queryKey: ['stock-unit-groups'] });
    qc.invalidateQueries({ queryKey: ['products'] });
  }
}

export function useIssueDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewDocumentInput) => api.post<IssueResponse>('/api/documents', input),
    onSuccess: ({ document }) => rememberDocument(qc, document),
  });
}

export type DocumentAction =
  | 'cancel'
  | 'confirm'
  | 'confirm-delivery'
  | 'sent'
  | 'issue'
  | 'credit-note'
  | 'delivery-note'
  | 'tax-document'
  | 'invoice-from-proforma'
  | 'invoice';

export function useDocumentAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: DocumentAction) =>
      api.post<IssueResponse>(`/api/documents/${id}/${action}`),
    onSuccess: ({ document, approvalRequired }) => {
      rememberDocument(qc, document);
      if (approvalRequired) qc.invalidateQueries({ queryKey: ['document-approval', id] });
      if (document.id !== id) qc.invalidateQueries({ queryKey: ['document', id] });
    },
  });
}

/** Koncept: uložit rozdělaný doklad bez čísla; vystaví se později. */
export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewDocumentInput) =>
      api.post<{ document: Document }>('/api/documents/drafts', input),
    onSuccess: ({ document }) => rememberDocument(qc, document),
  });
}

export function useUpdateDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<NewDocumentInput>) =>
      api.patch<{ document: Document }>(`/api/documents/${id}`, input),
    onSuccess: ({ document }) => rememberDocument(qc, document),
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useUpdateDocumentNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (internalNote: string) =>
      api.patch<{ document: Document }>(`/api/documents/${id}/note`, { internalNote }),
    onSuccess: ({ document }) => rememberDocument(qc, document),
  });
}

/** Přepočet otisku vystaveného dokladu: null = koncept nebo doklad před fází 5. */
export function useVerifyDocument(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['document-verify', id],
    queryFn: () =>
      api.get<{ valid: boolean | null; contentHash: string | null }>(`/api/documents/${id}/verify`),
    enabled: !!id && enabled,
    staleTime: 60_000,
  });
}

export function useSeriesAllocations(companyId: string, docType: string | null) {
  return useQuery({
    queryKey: ['series-allocations', companyId, docType],
    queryFn: async () =>
      (
        await api.get<{ allocations: NumberAllocation[] }>(
          `/api/companies/${companyId}/number-series/${docType}/allocations`,
        )
      ).allocations,
    enabled: !!companyId && !!docType,
  });
}

// ---- uzavřená období a funkce firmy ----

export function usePeriods(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['periods', companyId],
    queryFn: async () =>
      (await api.get<{ periods: AccountingPeriod[] }>(`/api/companies/${companyId}/periods`))
        .periods,
    enabled: !!companyId,
  });
}

export function useClosePeriod(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { from: string; to: string; note?: string }) =>
      api.post<{ period: AccountingPeriod }>(`/api/companies/${companyId}/periods`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['periods', companyId] }),
  });
}

export function useReopenPeriod(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ period: AccountingPeriod }>(`/api/companies/${companyId}/periods/${id}/reopen`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['periods', companyId] }),
  });
}

export function useFeatures(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['features', companyId],
    queryFn: async () =>
      (await api.get<{ features: FeatureInfo[] }>(`/api/companies/${companyId}/features`)).features,
    enabled: !!companyId,
  });
}

export function useSetFeature(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.put<{ features: FeatureInfo[] }>(`/api/companies/${companyId}/features/${key}`, {
        enabled,
      }),
    onSuccess: ({ features }) => {
      qc.setQueryData(['features', companyId], features);
      qc.invalidateQueries({ queryKey: meKey });
    },
  });
}

// ---- POHODA ----

export function useImportPohoda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) => api.postForm<ImportSummary>('/api/pohoda/import', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['number-series'] });
      qc.invalidateQueries({ queryKey: companiesKey });
    },
  });
}

// ---- platby ----

export interface PaymentsQuery {
  companyId?: string;
  unmatched?: boolean;
  month?: string;
}

export function usePayments(query: PaymentsQuery = {}) {
  const params = new URLSearchParams();
  if (query.companyId) params.set('companyId', query.companyId);
  if (query.unmatched) params.set('unmatched', '1');
  if (query.month) params.set('month', query.month);
  return useInfiniteList<'payments', PaymentListItem>(
    ['payments', query],
    '/api/payments',
    params,
    'payments',
  );
}

function invalidatePayments(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['payments'] });
  qc.invalidateQueries({ queryKey: ['documents'] });
  qc.invalidateQueries({ queryKey: ['overview'] });
  qc.invalidateQueries({ queryKey: ['contacts'] });
}

export function useAddDocumentPayment(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      amount: string;
      date: string;
      method: string;
      payerName: string;
      note: string;
      /** kurz platby u dokladu v cizí měně */
      exchangeRate?: string;
    }) =>
      api.post<{ payment: PaymentListItem; document: Document }>(
        `/api/documents/${documentId}/payments`,
        input,
      ),
    onSuccess: ({ document }) => {
      qc.setQueryData(['document', document.id], document);
      invalidatePayments(qc);
    },
  });
}

export function usePaymentCandidates(paymentId: string) {
  return useQuery({
    queryKey: ['payment-candidates', paymentId],
    queryFn: async () =>
      (await api.get<{ candidates: PaymentCandidate[] }>(`/api/payments/${paymentId}/candidates`))
        .candidates,
    enabled: !!paymentId,
  });
}

export function useMatchPayment(paymentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<{ payment: PaymentListItem; document: Document }>(
        `/api/payments/${paymentId}/match`,
        { documentId },
      ),
    onSuccess: ({ document }) => {
      qc.setQueryData(['document', document.id], document);
      invalidatePayments(qc);
    },
  });
}

export function useImportBankCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) => api.postForm<BankImportResult>('/api/payments/import', form),
    onSuccess: () => invalidatePayments(qc),
  });
}

// ---- Fáze 17: návrhy párování ----

export const suggestionsKey = ['payment-suggestions'] as const;

export function usePaymentSuggestions(companyId?: string | null) {
  return useQuery({
    queryKey: [...suggestionsKey, companyId ?? ''],
    queryFn: async () =>
      (
        await api.get<{ suggestions: PaymentSuggestionGroup[] }>(
          `/api/payments/suggestions?companyId=${companyId}`,
        )
      ).suggestions,
    enabled: !!companyId,
  });
}

function invalidateSuggestions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: suggestionsKey });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
  invalidatePayments(qc);
}

export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) =>
      api.post<{ payment: PaymentListItem; document: Document }>(
        `/api/payments/suggestions/${suggestionId}/accept`,
      ),
    onSuccess: () => invalidateSuggestions(qc),
  });
}

export function useSkipSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      api.post<{ skipped: number }>(`/api/payments/${paymentId}/suggestions/skip`),
    onSuccess: () => invalidateSuggestions(qc),
  });
}

export function useAcceptAllSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      api.post<{ accepted: number; needChoice: number; errors: string[] }>(
        '/api/payments/suggestions/accept-all',
        { companyId },
      ),
    onSuccess: () => invalidateSuggestions(qc),
  });
}

export function useOverview(companyId?: string) {
  return useQuery({
    queryKey: ['overview', companyId ?? 'all'],
    queryFn: () => api.get<Overview>(`/api/overview${companyId ? `?companyId=${companyId}` : ''}`),
    placeholderData: keepPreviousData,
  });
}

// ---- soubory ke stažení ----

/**
 * Stáhne soubor z API a nabídne ho prohlížeči. Název bere z hlavičky
 * content-disposition, aby se doklad uložil jako 26FV0018.pdf. Vrací ten název.
 */
async function download(path: string, fallbackName: string, what: string): Promise<string> {
  const res = await fetch(apiUrl(path), { credentials: 'include' });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new ApiError(res.status, what, data?.error?.message ?? `${what} se nepodařilo vytvořit`);
  }
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

/** PDF dokladu vytištěné serverem – stejná A4 jako v náhledu. */
export function downloadDocumentPdf(id: string, fallbackNumber: string): Promise<string> {
  return download(`/api/documents/${id}/pdf`, `${fallbackNumber}.pdf`, 'PDF');
}

/** XML jednoho dokladu pro import do POHODY. */
export function downloadDocumentXml(id: string, fallbackNumber: string): Promise<string> {
  return download(`/api/documents/${id}/pohoda.xml`, `${fallbackNumber}.xml`, 'XML');
}

/** ISDOC / ISDOCX / UBL (fáze 9). */
export function downloadDocumentIsdoc(id: string, fallbackNumber: string): Promise<string> {
  return download(`/api/documents/${id}/isdoc`, `${fallbackNumber}.isdoc`, 'ISDOC');
}
export function downloadDocumentIsdocx(id: string, fallbackNumber: string): Promise<string> {
  return download(`/api/documents/${id}/isdocx`, `${fallbackNumber}.isdocx`, 'ISDOCX');
}
export function downloadDocumentUbl(id: string, fallbackNumber: string): Promise<string> {
  return download(`/api/documents/${id}/ubl.xml`, `${fallbackNumber}-ubl.xml`, 'UBL');
}

// ---- sklad ----

export function useProducts(
  companyId?: string | null,
  q = '',
  inStock = false,
  warehouseId?: string | null,
  enabled = true,
  stockKind?: StockKind | null,
) {
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  if (q) params.set('q', q);
  if (inStock) params.set('inStock', 'true');
  if (warehouseId) params.set('warehouseId', warehouseId);
  if (stockKind) params.set('stockKind', stockKind);
  return useInfiniteList<'products', Product>(
    ['products', companyId ?? 'all', q, inStock, warehouseId ?? '', stockKind ?? ''],
    '/api/stock/products',
    params,
    'products',
    { enabled },
  );
}

export function useStockUnits(
  params: {
    companyId?: string | null;
    productId?: string;
    q?: string;
    status?: string;
    warehouseId?: string | null;
    /** stav zboží; prázdný řetězec = bez stavu */
    grade?: string;
    /** varianta; „none“ = kusy bez varianty */
    variantId?: string;
    /** fifo = nejdřív nejstarší naskladněné */
    sort?: 'fifo';
    /** část skladu */
    stockKind?: StockKind | null;
  },
  enabled = true,
) {
  const sp = new URLSearchParams();
  if (params.companyId) sp.set('companyId', params.companyId);
  if (params.productId) sp.set('productId', params.productId);
  if (params.q) sp.set('q', params.q);
  if (params.status) sp.set('status', params.status);
  if (params.warehouseId) sp.set('warehouseId', params.warehouseId);
  if (params.grade !== undefined) sp.set('grade', params.grade);
  if (params.variantId) sp.set('variantId', params.variantId);
  if (params.sort) sp.set('sort', params.sort);
  if (params.stockKind) sp.set('stockKind', params.stockKind);
  return useInfiniteList<'units', StockUnit>(
    ['stock-units', sp.toString()],
    '/api/stock/units',
    sp,
    'units',
    { enabled },
  );
}

/** Kusy skladem po skupinách (zboží + varianta + stav) – výběr na doklad. */
export function useStockUnitGroups(companyId: string | null, q: string, enabled = true) {
  return useQuery({
    queryKey: ['stock-unit-groups', companyId, q],
    queryFn: () =>
      api.get<{ groups: StockUnitGroup[] }>(
        `/api/stock/units/groups?companyId=${encodeURIComponent(companyId ?? '')}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: enabled && !!companyId,
    placeholderData: keepPreviousData,
  });
}

export function useStockSummary(
  companyId: string | null,
  from: string,
  to: string,
  warehouseId?: string | null,
  stockKind?: StockKind | null,
) {
  const wh = `${warehouseId ? `&warehouseId=${warehouseId}` : ''}${stockKind ? `&stockKind=${stockKind}` : ''}`;
  return useQuery({
    queryKey: ['stock-summary', companyId, from, to, warehouseId ?? '', stockKind ?? ''],
    queryFn: async () =>
      api.get<StockSummary>(`/api/stock/summary?companyId=${companyId}&from=${from}&to=${to}${wh}`),
    enabled: !!companyId,
  });
}

/** Přehled tří částí skladu (mobily a zboží / díly a materiál / majetek firmy). */
export function useStockKinds(companyId: string | null, warehouseId?: string | null) {
  return useQuery({
    queryKey: ['stock-kinds', companyId, warehouseId ?? ''],
    queryFn: async () =>
      api.get<{ kinds: StockKindOverview[] }>(
        `/api/stock/kinds?companyId=${companyId}${warehouseId ? `&warehouseId=${warehouseId}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: Record<string, unknown> }) =>
      api.patch<{ product: Product }>(`/api/stock/products/${v.id}`, v.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['stock-kinds'] });
      qc.invalidateQueries({ queryKey: ['stock-units'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
    },
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ product: Product }>('/api/stock/products', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

/** Naskladnění kusů s IMEI; vrací počet přijatých a přeskočených duplicit. */
export function useReceiveUnits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      offlinePost<{ received: number; skipped: string[] }>(
        '/api/stock/receive',
        data,
        `Příjem ${Array.isArray(data.serials) ? data.serials.length : 0} ks`,
        { received: 0, skipped: [] },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stock-units'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['stock-kinds'] });
    },
  });
}

// ---- sklad jako ledger (fáze 4) ----

const STOCK_KEYS = [
  'products',
  'stock-units',
  'stock-summary',
  'stock-kinds',
  'stock-movements',
  'warehouses',
];

/** Po každém pohybu se přepočítají všechny skladové pohledy. */
function useInvalidateStock() {
  const qc = useQueryClient();
  return () => {
    for (const key of STOCK_KEYS) qc.invalidateQueries({ queryKey: [key] });
  };
}

export function useWarehouses(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['warehouses', companyId],
    queryFn: async () =>
      (await api.get<{ warehouses: Warehouse[] }>(`/api/stock/warehouses?companyId=${companyId}`))
        .warehouses,
    enabled: !!companyId,
  });
}

export function useCreateWarehouse() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ warehouse: Warehouse }>('/api/stock/warehouses', data),
    onSuccess: invalidate,
  });
}

export function useUpdateWarehouse() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch<{ warehouse: Warehouse }>(`/api/stock/warehouses/${id}`, data),
    onSuccess: invalidate,
  });
}

export function useLocations(warehouseId: string | null | undefined) {
  return useQuery({
    queryKey: ['locations', warehouseId],
    queryFn: async () =>
      (
        await api.get<{ locations: WarehouseLocation[] }>(
          `/api/stock/warehouses/${warehouseId}/locations`,
        )
      ).locations,
    enabled: !!warehouseId,
  });
}

export function useCreateLocation(warehouseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string; name: string }) =>
      api.post<{ location: WarehouseLocation }>(
        `/api/stock/warehouses/${warehouseId}/locations`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations', warehouseId] }),
  });
}

export function useRemoveLocation(warehouseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/stock/warehouses/${warehouseId}/locations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations', warehouseId] }),
  });
}

export function useMovements(params: {
  companyId: string | null | undefined;
  warehouseId?: string | null;
  productId?: string;
  type?: string;
  q?: string;
  stockKind?: StockKind | null;
}) {
  const sp = new URLSearchParams();
  if (params.companyId) sp.set('companyId', params.companyId);
  if (params.warehouseId) sp.set('warehouseId', params.warehouseId);
  if (params.productId) sp.set('productId', params.productId);
  if (params.type) sp.set('type', params.type);
  if (params.q) sp.set('q', params.q);
  if (params.stockKind) sp.set('stockKind', params.stockKind);
  return useInfiniteList<'movements', StockMovement>(
    ['stock-movements', sp.toString()],
    '/api/stock/movements',
    sp,
    'movements',
    { enabled: !!params.companyId },
  );
}

export function useReceiveQuantity() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      offlinePost<{ move: StockMovement | null }>(
        '/api/stock/receive-quantity',
        data,
        `Příjem množství ${String(data.quantity ?? '')}`,
        { move: null },
      ),
    onSuccess: invalidate,
  });
}

export function useTransfer() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ out: StockMovement; in: StockMovement }>('/api/stock/transfer', data),
    onSuccess: invalidate,
  });
}

export function useAdjust() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ movement: StockMovement }>('/api/stock/adjust', data),
    onSuccess: invalidate,
  });
}

export function useUnitStatus() {
  const invalidate = useInvalidateStock();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; status: string; reason: string }) =>
      offlinePost<{ unit: StockUnit | null }>(
        `/api/stock/units/${id}/status`,
        data,
        `Stav kusu → ${data.status}`,
        {
          unit: null,
        },
      ),
    onSuccess: (_r, v) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['unit-history', v.id] });
    },
  });
}

/** Skener: kód → kus / produkt / nic. Mutace, protože se volá na každé načtení. */
export function useScan(companyId: string) {
  return useMutation({
    mutationFn: (code: string) =>
      api.get<ScanResult>(
        `/api/stock/scan/${encodeURIComponent(code.trim())}?companyId=${encodeURIComponent(companyId)}`,
      ),
  });
}

/** Nákupní doklady kusu: přijatá faktura, výkupní listy, nahrané doklady. */
export function useUnitDocs(unitId: string | null | undefined) {
  return useQuery({
    queryKey: ['unit-docs', unitId],
    queryFn: () => api.get<StockUnitDocs>(`/api/stock/units/${unitId}/docs`),
    enabled: !!unitId,
  });
}

function useInvalidateUnitDocs() {
  const qc = useQueryClient();
  return (unitId: string) => {
    qc.invalidateQueries({ queryKey: ['unit-docs', unitId] });
    // vykřičník „chybí nákupní doklad“ u faktur
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['document'] });
  };
}

export interface BuyoutInput {
  date?: string;
  sellerName: string;
  sellerAddress?: string;
  sellerId?: string;
  price?: string;
  note?: string;
}

export function useCreateBuyout() {
  const invalidate = useInvalidateUnitDocs();
  return useMutation({
    mutationFn: ({ unitId, ...body }: BuyoutInput & { unitId: string }) =>
      api.post<{ doc: StockUnitDoc }>(`/api/stock/units/${unitId}/buyout`, body),
    onSuccess: (_r, v) => invalidate(v.unitId),
  });
}

export function useUploadUnitDoc() {
  const invalidate = useInvalidateUnitDocs();
  return useMutation({
    mutationFn: ({ unitId, file }: { unitId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api.postForm<{ doc: StockUnitDoc }>(`/api/stock/units/${unitId}/docs`, form);
    },
    onSuccess: (_r, v) => invalidate(v.unitId),
  });
}

export function useDeleteUnitDoc() {
  const invalidate = useInvalidateUnitDocs();
  return useMutation({
    mutationFn: ({ unitId, id }: { unitId: string; id: string }) =>
      api.delete(`/api/stock/units/${unitId}/docs/${id}`),
    onSuccess: (_r, v) => invalidate(v.unitId),
  });
}

export function useUnitHistory(unitId: string | null | undefined) {
  return useQuery({
    queryKey: ['unit-history', unitId],
    queryFn: () =>
      api.get<{ unit: StockUnit; movements: StockMovement[] }>(
        `/api/stock/units/${unitId}/history`,
      ),
    enabled: !!unitId,
  });
}

export function useStockTakes(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['stocktakes', companyId],
    queryFn: async () =>
      (await api.get<{ stockTakes: StockTake[] }>(`/api/stock/stocktakes?companyId=${companyId}`))
        .stockTakes,
    enabled: !!companyId,
  });
}

export function useStockTake(id: string | undefined) {
  return useQuery({
    queryKey: ['stocktake', id],
    queryFn: async () =>
      (await api.get<{ stockTake: StockTake }>(`/api/stock/stocktakes/${id}`)).stockTake,
    enabled: !!id,
  });
}

export function useStartStockTake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; warehouseId: string; note?: string }) =>
      api.post<{ stockTake: StockTake }>('/api/stock/stocktakes', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stocktakes'] }),
  });
}

export function useCountStockTake(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lines: { lineId: string; counted: string; note?: string }[]) =>
      api.put<{ stockTake: StockTake }>(`/api/stock/stocktakes/${id}/lines`, { lines }),
    onSuccess: ({ stockTake }) => qc.setQueryData(['stocktake', id], stockTake),
  });
}

export function useCloseStockTake(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: () => api.post<{ stockTake: StockTake }>(`/api/stock/stocktakes/${id}/close`, {}),
    onSuccess: ({ stockTake }) => {
      qc.setQueryData(['stocktake', id], stockTake);
      qc.invalidateQueries({ queryKey: ['stocktakes'] });
      invalidate();
    },
  });
}

// ---- měny, kurzy, účty, registrace (fáze 6) ----

export function useRates(companyId: string | null | undefined, base = 'CZK', date?: string) {
  return useQuery({
    queryKey: ['rates', companyId, base, date ?? ''],
    queryFn: async () =>
      (
        await api.get<{ rates: ExchangeRateInfo[] }>(
          `/api/rates?companyId=${companyId}&base=${base}${date ? `&date=${date}` : ''}`,
        )
      ).rates,
    enabled: !!companyId,
  });
}

/** Kurz k datu pro nový doklad; chybějící kurz vrací 409 a formulář nabídne ruční zadání. */
export function fetchRate(companyId: string, base: string, quote: string, date: string) {
  return api.get<{ rate: { rate: string; date: string; source: string } }>(
    `/api/rates/one?companyId=${companyId}&base=${base}&quote=${quote}&date=${date}`,
  );
}

export function useSetRate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      base?: string;
      quote: string;
      date: string;
      rate: string;
      note?: string;
    }) => api.put<{ rate: ExchangeRateInfo }>('/api/rates', { companyId, ...data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rates'] }),
  });
}

export function useSyncRates(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date?: string) =>
      api.post<{ synced: { source: string; date: string; count: number }[] }>('/api/rates/sync', {
        companyId,
        ...(date ? { date } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rates'] }),
  });
}

export function useBankAccounts(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['bank-accounts', companyId],
    queryFn: async () =>
      (await api.get<{ bankAccounts: BankAccount[] }>(`/api/companies/${companyId}/bank-accounts`))
        .bankAccounts,
    enabled: !!companyId,
  });
}

export function useCreateBankAccount(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ bankAccount: BankAccount }>(`/api/companies/${companyId}/bank-accounts`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts', companyId] });
      qc.invalidateQueries({ queryKey: companiesKey });
    },
  });
}

export function useUpdateBankAccount(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch<{ bankAccount: BankAccount }>(
        `/api/companies/${companyId}/bank-accounts/${id}`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts', companyId] });
      qc.invalidateQueries({ queryKey: companiesKey });
    },
  });
}

export function useRemoveBankAccount(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/companies/${companyId}/bank-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts', companyId] }),
  });
}

export function useTaxRegistrations(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['tax-registrations', companyId],
    queryFn: async () =>
      (
        await api.get<{ registrations: TaxRegistration[] }>(
          `/api/companies/${companyId}/tax-registrations`,
        )
      ).registrations,
    enabled: !!companyId,
  });
}

export function useAllocatePayment(paymentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (allocations: { documentId: string; amount: string }[]) =>
      api.post<{ payment: PaymentListItem; documents: Document[] }>(
        `/api/payments/${paymentId}/allocations`,
        { allocations },
      ),
    onSuccess: ({ documents }) => {
      for (const d of documents) qc.setQueryData(['document', d.id], d);
      invalidatePayments(qc);
    },
  });
}

// ---- fáze 7: podklady pro účetní, balíček, deník ----

const reportPath = (name: string, companyId: string, period: string, format = 'json') =>
  `/api/reports/${name}?companyId=${companyId}&period=${period}&format=${format}`;

export function useVatReturn(companyId: string, period: string) {
  return useQuery({
    queryKey: ['report', 'vat-return', companyId, period],
    queryFn: () => api.get<VatReturnResponse>(reportPath('vat-return', companyId, period)),
    enabled: !!companyId && !!period,
  });
}

export function useVatOverview(companyId: string, year: number, enabled = true) {
  return useQuery({
    queryKey: ['report', 'vat-overview', companyId, year],
    queryFn: () =>
      api.get<VatOverview>(`/api/reports/vat-overview?companyId=${companyId}&year=${year}`),
    enabled: enabled && !!companyId,
  });
}

/** Ručně zapsaný odvod DPH (out) nebo vrácený nadměrný odpočet (in). */
export function useAddVatPayment(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      period: string;
      date: string;
      amount: string;
      direction: 'in' | 'out';
    }) =>
      api.post<{ id: string; period: string }>('/api/reports/vat-payments', {
        companyId,
        ...input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report', 'vat-overview', companyId] });
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export function useControlStatement(companyId: string, period: string, enabled = true) {
  return useQuery({
    queryKey: ['report', 'control-statement', companyId, period],
    queryFn: async () =>
      (
        await api.get<{ statement: ControlStatement }>(
          reportPath('control-statement', companyId, period),
        )
      ).statement,
    enabled: enabled && !!companyId && !!period,
  });
}

export function useEcSales(companyId: string, period: string, enabled = true) {
  return useQuery({
    queryKey: ['report', 'ec-sales', companyId, period],
    queryFn: async () =>
      (await api.get<{ list: EcSalesList }>(reportPath('ec-sales', companyId, period))).list,
    enabled: enabled && !!companyId && !!period,
  });
}

export function useFxDifferences(companyId: string, period: string) {
  return useQuery({
    queryKey: ['report', 'fx-differences', companyId, period],
    queryFn: () => api.get<FxDifferences>(reportPath('fx-differences', companyId, period)),
    enabled: !!companyId && !!period,
  });
}

/** Stažení souboru z API (CSV/XML/ZIP) – cookie jde s požadavkem, prohlížeč uloží přílohu. */
export async function downloadFile(path: string, init?: RequestInit): Promise<string> {
  const res = await fetch(apiUrl(path), { credentials: 'include', ...init });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      res.status,
      data?.error?.code ?? 'ERROR',
      data?.error?.message ?? `Chyba ${res.status}`,
    );
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'soubor';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return name;
}

export const reportFileUrl = (
  name: string,
  companyId: string,
  period: string,
  format: 'csv' | 'xml',
) => reportPath(name, companyId, period, format);

export function useExportPackages(companyId: string) {
  return useQuery({
    queryKey: ['export-packages', companyId],
    queryFn: async () =>
      (await api.get<{ packages: ExportPackage[] }>(`/api/exports/packages?companyId=${companyId}`))
        .packages,
    enabled: !!companyId,
  });
}

export function useBuildPackage(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { period: string; sentTo?: string }) =>
      downloadFile('/api/exports/package', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ companyId, ...input }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['export-packages', companyId] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useJournal(companyId: string, period: string, enabled = true) {
  return useQuery({
    queryKey: ['journal', companyId, period],
    queryFn: async () =>
      (
        await api.get<{ journal: Journal }>(
          `/api/accounting/journal?companyId=${companyId}&period=${period}`,
        )
      ).journal,
    enabled: enabled && !!companyId && !!period,
  });
}

export function useRebuildJournal(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      api.post<{ period: unknown; entries: number }>('/api/accounting/rebuild', {
        companyId,
        period,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal', companyId] }),
  });
}

// ---- fáze 8: dashboard, reporty, hledání, pohledy ----

export function useDashboard(companyId: string | null | undefined, period?: string) {
  return useQuery({
    queryKey: ['dashboard', companyId, period ?? ''],
    queryFn: () =>
      api.get<Dashboard>(
        `/api/dashboard?companyId=${companyId}${period ? `&period=${period}` : ''}`,
      ),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });
}

export function useSaveDashboard(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (widgets: WidgetKey[]) =>
      api.put<Dashboard>('/api/dashboard/layout', { companyId, widgets }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', companyId] }),
  });
}

export function useResetDashboard(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<Dashboard>(`/api/dashboard/layout?companyId=${companyId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', companyId] }),
  });
}

export function reportUrl(
  kind: ReportKind,
  companyId: string,
  f: ReportFilters,
  format: 'json' | 'csv' | 'xlsx' | 'pdf' = 'json',
) {
  const params = new URLSearchParams({ companyId, from: f.from, to: f.to, format });
  for (const k of ['warehouseId', 'contactId', 'productId', 'currency'] as const) {
    if (f[k]) params.set(k, f[k] as string);
  }
  return `/api/reports/${kind}?${params.toString()}`;
}

export function useReport<T = unknown>(
  kind: ReportKind,
  companyId: string | null | undefined,
  f: ReportFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: ['report', kind, companyId, f],
    queryFn: () => api.get<ReportOut<T>>(reportUrl(kind, companyId ?? '', f)),
    enabled: enabled && !!companyId && !!f.from && !!f.to,
    placeholderData: keepPreviousData,
  });
}

export function useSearch(q: string, companyId?: string | null) {
  return useQuery({
    queryKey: ['search', q, companyId ?? 'all'],
    queryFn: () =>
      api.get<SearchResult>(
        `/api/search?q=${encodeURIComponent(q)}${companyId ? `&companyId=${companyId}` : ''}`,
      ),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export function useViews(page: string, companyId?: string | null) {
  return useQuery({
    queryKey: ['views', page, companyId ?? ''],
    queryFn: async () =>
      (
        await api.get<{ views: SavedView[] }>(
          `/api/views?page=${page}${companyId ? `&companyId=${companyId}` : ''}`,
        )
      ).views,
  });
}

export function useSaveView(page: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; companyId?: string; filters: Record<string, unknown> }) =>
      api.post<{ view: SavedView }>('/api/views', { page, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['views', page] }),
  });
}

export function useDeleteView(page: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/views/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['views', page] }),
  });
}

// ---- fáze 9: webhooky, import, ISDOC ----

const webhooksKey = (companyId: string) => ['webhooks', companyId] as const;

export function useWebhooks(companyId: string) {
  return useQuery({
    queryKey: webhooksKey(companyId),
    queryFn: async () =>
      (await api.get<{ webhooks: Webhook[] }>(`/api/companies/${companyId}/webhooks`)).webhooks,
    enabled: !!companyId,
  });
}

export function useCreateWebhook(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { url: string; name: string; events: WebhookEvent[] }) =>
      api.post<{ webhook: Webhook; secret: string }>(`/api/companies/${companyId}/webhooks`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(companyId) }),
  });
}

export function useUpdateWebhook(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: {
      id: string;
      input: { url?: string; name?: string; events?: WebhookEvent[]; isActive?: boolean };
    }) => api.patch<{ webhook: Webhook }>(`/api/companies/${companyId}/webhooks/${p.id}`, p.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(companyId) }),
  });
}

export function useDeleteWebhook(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/companies/${companyId}/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(companyId) }),
  });
}

export function useWebhookDeliveries(companyId: string, webhookId: string | null) {
  return useQuery({
    queryKey: ['webhook-deliveries', companyId, webhookId ?? ''],
    queryFn: async () =>
      (
        await api.get<{ deliveries: WebhookDelivery[] }>(
          `/api/companies/${companyId}/webhooks/${webhookId}/deliveries`,
        )
      ).deliveries,
    enabled: !!companyId && !!webhookId,
    refetchInterval: 15_000,
  });
}

export function useTestWebhook(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ delivery: WebhookDelivery }>(
          `/api/companies/${companyId}/webhooks/${id}/test`,
        )
      ).delivery,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-deliveries', companyId] }),
  });
}

export function useRetryDelivery(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { webhookId: string; deliveryId: string }) =>
      (
        await api.post<{ delivery: WebhookDelivery }>(
          `/api/companies/${companyId}/webhooks/${p.webhookId}/deliveries/${p.deliveryId}/retry`,
        )
      ).delivery,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhook-deliveries', companyId] });
      qc.invalidateQueries({ queryKey: webhooksKey(companyId) });
    },
  });
}

export function useAnalyzeImport(companyId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set('companyId', companyId);
      form.append('file', file, file.name);
      return api.postForm<ImportAnalysis>('/api/imports/analyze', form);
    },
  });
}

export interface ImportRunInput {
  companyId: string;
  uploadId: string;
  target: ImportTarget;
  mapping: Record<string, number | null>;
  options?: { header?: boolean; warehouseId?: string; date?: string };
}

export function useImportPreview() {
  return useMutation({
    mutationFn: (input: ImportRunInput) => api.post<ImportPreview>('/api/imports/preview', input),
  });
}

export function useImportRun(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportRunInput) => api.post<ImportResult>('/api/imports/run', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', companyId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stock'] });
      invalidatePayments(qc);
    },
  });
}

export function useImportHistory(companyId: string) {
  return useQuery({
    queryKey: ['imports', companyId],
    queryFn: async () =>
      (await api.get<{ imports: ImportBatch[] }>(`/api/imports?companyId=${companyId}`)).imports,
    enabled: !!companyId,
  });
}

export function useImportIsdoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { companyId: string; file: File }) => {
      const form = new FormData();
      form.set('companyId', p.companyId);
      form.append('file', p.file, p.file.name);
      return api.postForm<{ document: Document }>('/api/documents/import-isdoc', form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ---- Telegram (fáze 11) ----

const telegramStatusKey = ['telegram-status'] as const;
const telegramPrefsKey = (companyId: string) => ['telegram-preferences', companyId] as const;

export function useTelegramStatus() {
  return useQuery({
    queryKey: telegramStatusKey,
    queryFn: () => api.get<TelegramStatus>('/api/telegram/status'),
  });
}

export function useTelegramLinkCode() {
  return useMutation({
    mutationFn: () => api.post<TelegramLinkCode>('/api/telegram/link-code'),
  });
}

export function useTelegramUnlink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/api/telegram/link'),
    onSuccess: () => qc.invalidateQueries({ queryKey: telegramStatusKey }),
  });
}

export function useTelegramMute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (muted: boolean) => api.put<{ muted: boolean }>('/api/telegram/mute', { muted }),
    onSuccess: () => qc.invalidateQueries({ queryKey: telegramStatusKey }),
  });
}

export function useTelegramTest() {
  return useMutation({
    mutationFn: () => api.post<{ status: string; error: string | null }>('/api/telegram/test'),
  });
}

export function useTelegramPreferences(companyId: string) {
  return useQuery({
    queryKey: telegramPrefsKey(companyId),
    queryFn: async () =>
      (
        await api.get<{ preferences: TelegramPreference[] }>(
          `/api/telegram/preferences?companyId=${encodeURIComponent(companyId)}`,
        )
      ).preferences,
    enabled: !!companyId,
  });
}

export function useUpdateTelegramPreferences(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      items: { event: string; channel?: 'inapp' | 'telegram' | 'email'; enabled: boolean }[];
    }) =>
      api.put<{ preferences: TelegramPreference[] }>('/api/telegram/preferences', {
        companyId,
        ...input,
      }),
    onSuccess: (data) => qc.setQueryData(telegramPrefsKey(companyId), data.preferences),
  });
}

// ---- Fáze 12: notifikace ----

const notificationsKey = ['notifications'] as const;

export function useNotifications(unread = false) {
  return useQuery({
    queryKey: [...notificationsKey, unread],
    queryFn: () =>
      api.get<{ unread: number; notifications: AppNotification[] }>(
        `/api/notifications${unread ? '?unread=true' : ''}`,
      ),
    refetchInterval: 60_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: [...notificationsKey, 'count'],
    queryFn: () => api.get<{ unread: number }>('/api/notifications/unread-count'),
    refetchInterval: 45_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<{ marked: number }>(`/api/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKey }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.put<{ marked: number }>('/api/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKey }),
  });
}

// ---- Fáze 12: e-maily ----

const emailsKey = (companyId: string, entityId?: string) =>
  ['emails', companyId, entityId ?? ''] as const;

export function useEmails(companyId: string, entityType?: string, entityId?: string) {
  const qs = new URLSearchParams({ companyId });
  if (entityType) qs.set('entityType', entityType);
  if (entityId) qs.set('entityId', entityId);
  return useQuery({
    queryKey: emailsKey(companyId, entityId),
    queryFn: () => api.get<{ emails: EmailRecord[]; configured: boolean }>(`/api/emails?${qs}`),
    enabled: !!companyId,
  });
}

export function useSendDocument(id: string, companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      to?: string;
      cc?: string;
      subject?: string;
      message?: string;
      attachIsdoc?: boolean;
    }) => api.post<{ email: EmailRecord }>(`/api/documents/${id}/send`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailsKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['document', id] });
    },
  });
}

export function useRemindDocument(id: string, companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { to?: string; message?: string }) =>
      api.post<{ email: EmailRecord }>(`/api/documents/${id}/remind`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailsKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['document', id] });
    },
  });
}

export function useRetryEmail(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ email: EmailRecord }>(`/api/emails/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emails', companyId] }),
  });
}

const emailTemplatesKey = (companyId: string) => ['email-templates', companyId] as const;

export function useEmailTemplates(companyId: string) {
  return useQuery({
    queryKey: emailTemplatesKey(companyId),
    queryFn: async () =>
      (
        await api.get<{ templates: EmailTemplateRecord[] }>(
          `/api/companies/${companyId}/email-templates`,
        )
      ).templates,
    enabled: !!companyId,
  });
}

export function useSaveEmailTemplate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; locale: 'cs' | 'en'; subject: string; bodyText: string }) =>
      api.put<{ templates: EmailTemplateRecord[] }>(
        `/api/companies/${companyId}/email-templates`,
        input,
      ),
    onSuccess: (data) => qc.setQueryData(emailTemplatesKey(companyId), data.templates),
  });
}

export function useResetEmailTemplate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; locale: 'cs' | 'en' }) =>
      api.delete<{ templates: EmailTemplateRecord[] }>(
        `/api/companies/${companyId}/email-templates/${input.key}/${input.locale}`,
      ),
    onSuccess: (data) => qc.setQueryData(emailTemplatesKey(companyId), data.templates),
  });
}

export function usePreviewEmailTemplate(companyId: string) {
  return useMutation({
    mutationFn: (input: {
      key: string;
      locale: 'cs' | 'en';
      subject?: string;
      bodyText?: string;
    }) =>
      api.post<{ subject: string; text: string; html: string }>(
        `/api/companies/${companyId}/email-templates/preview`,
        input,
      ),
  });
}

// ---- Fáze 12: automatizace ----

const rulesKey = (companyId: string) => ['automation-rules', companyId] as const;

export function useAutomationMeta() {
  return useQuery({
    queryKey: ['automation-meta'],
    queryFn: () => api.get<AutomationMeta>('/api/automation/meta'),
    staleTime: 600_000,
  });
}

export function useAutomationRules(companyId: string) {
  return useQuery({
    queryKey: rulesKey(companyId),
    queryFn: async () =>
      (await api.get<{ rules: AutomationRule[] }>(`/api/automation/rules?companyId=${companyId}`))
        .rules,
    enabled: !!companyId,
  });
}

export function useCreateRule(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Omit<
        AutomationRule,
        'id' | 'companyId' | 'runCount' | 'lastRunAt' | 'createdAt' | 'updatedAt'
      >,
    ) => api.post<{ rule: AutomationRule }>('/api/automation/rules', { companyId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey(companyId) }),
  });
}

export function useRuleFromTemplate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      api.post<{ rule: AutomationRule }>('/api/automation/rules/from-template', { companyId, key }),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey(companyId) }),
  });
}

export function useUpdateRule(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: { id: string } & Partial<
      Pick<AutomationRule, 'name' | 'trigger' | 'conditions' | 'actions' | 'enabled' | 'priority'>
    >) => api.patch<{ rule: AutomationRule }>(`/api/automation/rules/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey(companyId) }),
  });
}

export function useDeleteRule(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/automation/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey(companyId) }),
  });
}

export function useAutomationRuns(companyId: string) {
  return useQuery({
    queryKey: ['automation-runs', companyId],
    queryFn: async () =>
      (
        await api.get<{ runs: AutomationRun[] }>(
          `/api/automation/runs?companyId=${companyId}&limit=50`,
        )
      ).runs,
    enabled: !!companyId,
  });
}

export function useRunDaily(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ overdue: number; lowStock: number }>('/api/automation/run-daily', { companyId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-runs', companyId] });
      qc.invalidateQueries({ queryKey: rulesKey(companyId) });
    },
  });
}

// ---- Fáze 12: schvalování ----

const approvalsKey = (companyId: string) => ['approvals', companyId] as const;

export function useApprovals(companyId: string, status?: string) {
  return useQuery({
    queryKey: [...approvalsKey(companyId), status ?? 'all'],
    queryFn: async () =>
      (
        await api.get<{ approvals: ApprovalRequest[] }>(
          `/api/approvals?companyId=${companyId}${status ? `&status=${status}` : ''}`,
        )
      ).approvals,
    enabled: !!companyId,
  });
}

export function useDocumentApproval(id: string) {
  return useQuery({
    queryKey: ['document-approval', id],
    queryFn: async () =>
      (await api.get<{ approval: ApprovalRequest | null }>(`/api/documents/${id}/approval`))
        .approval,
    enabled: !!id,
  });
}

export function useDecideApproval(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: 'approve' | 'reject';
      note?: string;
    }) =>
      api.post<{ approval: ApprovalRequest; document?: Document }>(
        `/api/approvals/${id}/${decision}`,
        { note: note ?? '' },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: approvalsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-approval', data.approval.entityId] });
      if (data.document) rememberDocument(qc, data.document);
    },
  });
}

// ---- Fáze 12: komentáře ----

const commentsKey = (entityType: string, entityId: string) =>
  ['comments', entityType, entityId] as const;

export function useComments(entityType: string, entityId: string) {
  return useQuery({
    queryKey: commentsKey(entityType, entityId),
    queryFn: async () =>
      (
        await api.get<{ comments: Comment[] }>(
          `/api/comments?entityType=${entityType}&entityId=${entityId}`,
        )
      ).comments,
    enabled: !!entityId,
  });
}

export function useAddComment(companyId: string, entityType: string, entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<{ comment: Comment }>('/api/comments', { companyId, entityType, entityId, body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(entityType, entityId) }),
  });
}

export function useDeleteComment(entityType: string, entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(entityType, entityId) }),
  });
}

// ---- Fáze 13: MFA ----

const mfaKey = ['mfa'] as const;

export function useVerifyMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { challenge: string; code: string }) =>
      api.post<{ user: User }>('/api/auth/mfa/verify', input),
    onSuccess: ({ user }) => {
      qc.setQueryData(meKey, { user });
      qc.invalidateQueries({ queryKey: meKey });
    },
  });
}

export function useMfaStatus() {
  return useQuery({ queryKey: mfaKey, queryFn: () => api.get<MfaStatus>('/api/auth/mfa') });
}

export function useSetupMfa() {
  return useMutation({
    mutationFn: () =>
      api.post<{ secret: string; otpauth: string; qrSvg: string }>('/api/auth/mfa/setup'),
  });
}

export function useEnableMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/enable', { code }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mfaKey });
      qc.invalidateQueries({ queryKey: meKey });
    },
  });
}

export function useDisableMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { password: string; code: string }) =>
      api.post<void>('/api/auth/mfa/disable', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mfaKey });
      qc.invalidateQueries({ queryKey: meKey });
    },
  });
}

export function useRegenerateRecoveryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/recovery-codes', { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mfaKey }),
  });
}

export function useCompanySecurity(companyId: string) {
  return useQuery({
    queryKey: ['company-security', companyId],
    queryFn: () => api.get<{ mfaRequiredRoles: string[] }>(`/api/companies/${companyId}/security`),
    enabled: !!companyId,
  });
}

export function useSetCompanySecurity(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mfaRequiredRoles: string[]) =>
      api.put<{ mfaRequiredRoles: string[] }>(`/api/companies/${companyId}/security`, {
        mfaRequiredRoles,
      }),
    onSuccess: (data) => qc.setQueryData(['company-security', companyId], data),
  });
}

// ---- Fáze 13: provoz / admin ----

/** Chybějící pole PEPPOL/UBL dokladu (fáze 16 – panel v detailu dokladu). */
export function useUblCheck(id: string, enabled = true) {
  return useQuery({
    queryKey: ['ubl-check', id],
    queryFn: () => api.get<UblCheck>(`/api/documents/${id}/ubl-check`),
    enabled: !!id && enabled,
    retry: false,
  });
}

export function useOpsPerf(enabled = true) {
  return useQuery({
    queryKey: ['ops-perf'],
    queryFn: () => api.get<OpsPerf>('/api/ops/perf'),
    enabled,
    retry: false,
    refetchInterval: 30_000,
  });
}

export function useOpsStatus() {
  return useQuery({
    queryKey: ['ops-status'],
    queryFn: () => api.get<OpsStatus>('/api/ops/status'),
    retry: false,
    refetchInterval: 60_000,
  });
}

export function useBackups(enabled = true) {
  return useQuery({
    queryKey: ['ops-backups'],
    queryFn: () => api.get<{ configured: boolean; backups: BackupRecord[] }>('/api/ops/backups'),
    enabled,
  });
}

export function useRunBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ backup: BackupRecord }>('/api/ops/backup'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ops-backups'] });
      qc.invalidateQueries({ queryKey: ['ops-status'] });
    },
  });
}

export function useVerifyBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ backup: BackupRecord }>(`/api/ops/backups/${id}/verify`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops-backups'] }),
  });
}

export function useAdminQueues(enabled = true) {
  return useQuery({
    queryKey: ['ops-queues'],
    queryFn: () => api.get<AdminQueues>('/api/ops/queues'),
    enabled,
  });
}

export function useRetryQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'webhooks' | 'emails' | 'telegram'; id: string }) =>
      api.post<unknown>(`/api/ops/queues/${input.kind}/${input.id}/retry`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ops-queues'] }),
  });
}

export function useAdminLogins(enabled = true) {
  return useQuery({
    queryKey: ['ops-logins'],
    queryFn: () => api.get<AdminLogins>('/api/ops/logins'),
    enabled,
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`/api/ops/users/${id}/unlock`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops-logins'] }),
  });
}

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: ['ops-users'],
    queryFn: () => api.get<{ users: AdminUser[] }>('/api/ops/users'),
    enabled,
  });
}

export function useAnonymizeUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: string; anonymizedAt: string }>(`/api/ops/users/${id}/anonymize`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops-users'] }),
  });
}

export function useRetentionPolicies(enabled = true) {
  return useQuery({
    queryKey: ['ops-retention'],
    queryFn: () =>
      api.get<{
        policies: RetentionPolicy[];
        days: Record<string, number>;
        last: RetentionRun | null;
      }>('/api/ops/retention'),
    enabled,
  });
}

export function useRunRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ id: string; status: string; detail: Record<string, number> }>(
        '/api/ops/retention/run',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops-retention'] }),
  });
}

// ---- Fáze 13: GDPR a archiv ----

export function useAnonymizeContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ contact: Contact }>(`/api/contacts/${id}/anonymize`),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact', id] });
    },
  });
}

export function useDocumentArchive(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['document-archive', id],
    queryFn: () =>
      api.get<{
        archives: DocumentArchiveRecord[];
        integrity: { id: string; kind: string; valid: boolean }[];
      }>(`/api/documents/${id}/archive`),
    enabled: !!id && enabled,
  });
}

export function useArchiveDocument(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ archives: DocumentArchiveRecord[] }>(`/api/documents/${id}/archive`, {
        isdoc: true,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-archive', id] }),
  });
}

// ---- Fáze 14: jazyk aplikace ----

export function useSetUserLocale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: 'cs' | 'en') => api.patch<{ user: User }>('/api/auth/me', { locale }),
    onSuccess: ({ user }) => {
      qc.setQueryData(meKey, (old: MeResponse | undefined) => (old ? { ...old, user } : old));
    },
  });
}

// ---- přijaté faktury: analyzátor, zápis s naskladněním, párování ----

/** Nahraje fakturu (PDF, fotka, ISDOC) a vrátí návrh ke kontrole. */
export function useAnalyzePurchase() {
  return useMutation({
    mutationFn: (form: FormData) => api.postForm<PurchaseProposal>('/api/purchases/analyze', form),
  });
}

/** Zapíše přijatou fakturu a naskladní zboží do regálů. */
export function useCommitPurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ document: Document; received: { units: number; quantity: string } }>(
        '/api/purchases',
        body,
      ),
    onSuccess: ({ document }) => {
      qc.setQueryData(['document', document.id], document);
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stock-units'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['stock-kinds'] });
      qc.invalidateQueries({ queryKey: ['locations'] });
      qc.invalidateQueries({ queryKey: ['business'] });
      qc.invalidateQueries({ queryKey: ['inbox-items'] });
    },
  });
}

/** Kusy dokladu: kam se naskladnily, komu se prodaly / odkud byly nakoupené. */
export function useStockTrace(documentId: string, enabled = true) {
  return useQuery({
    queryKey: ['stock-trace', documentId],
    queryFn: () => api.get<StockTrace>(`/api/purchases/trace/${documentId}`),
    enabled: enabled && !!documentId,
  });
}

/** Výsledky podnikání pro Přehled: období YYYY, YYYY-MM nebo YYYY-Qn. */
export function useBusiness(companyId: string | null | undefined, period: string) {
  return useQuery({
    queryKey: ['business', companyId, period],
    queryFn: () =>
      api.get<BusinessSummary>(
        `/api/reports/business?companyId=${companyId}&period=${encodeURIComponent(period)}`,
      ),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Právní forma a výdajový paušál pro odhad daně z příjmů. */
export function useSetTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      ...body
    }: {
      companyId: string;
      legalForm: 'sro' | 'osvc';
      flatExpensePct: number;
      vatPayer?: boolean;
      dic?: string;
      vatPeriod?: 'month' | 'quarter';
      vatPayerFrom?: string;
    }) =>
      api.put<{
        company: Pick<
          Company,
          'id' | 'legalForm' | 'flatExpensePct' | 'vatPayer' | 'dic' | 'vatPeriod'
        >;
      }>(`/api/reports/business/tax-profile/${companyId}`, body),
    onSuccess: ({ company }) => {
      qc.setQueryData<Company[]>(companiesKey, (old) =>
        old?.map((c) => (c.id === company.id ? { ...c, ...company } : c)),
      );
      qc.invalidateQueries({ queryKey: ['business'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ---- zakázky a opravy ----

export type JobView = 'active' | 'ready' | 'done' | 'overdue' | 'all';

/** Seznam zakázek firmy: aktivní, k vyzvednutí, hotové, po termínu, vše; hledání. */
export function useJobs(
  companyId: string | null | undefined,
  view: JobView,
  q = '',
  opts: { contactId?: string; enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  params.set('view', view);
  if (q) params.set('q', q);
  if (opts.contactId) params.set('contactId', opts.contactId);
  return useInfiniteList<'jobs', Job>(
    ['jobs', companyId ?? '', view, q, opts.contactId ?? ''],
    '/api/jobs',
    params,
    'jobs',
    { enabled: !!companyId && (opts.enabled ?? true) },
  );
}

/** Co hoří: po termínu, dnes, k vyzvednutí, bez faktury – přehled a seznam. */
export function useJobsSummary(companyId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['jobs-summary', companyId],
    queryFn: () => api.get<JobsSummary>(`/api/jobs/summary?companyId=${companyId}`),
    enabled: !!companyId && enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: ['job', id],
    queryFn: async () => (await api.get<{ job: Job }>(`/api/jobs/${id}`)).job,
    enabled: !!id,
  });
}

/** Technici: členové firmy se správou zakázek. */
export function useJobAssignees(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['job-assignees', companyId],
    queryFn: async () =>
      (
        await api.get<{ assignees: { userId: string; email: string }[] }>(
          `/api/jobs/assignees?companyId=${companyId}`,
        )
      ).assignees,
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

export interface JobFormInput {
  kind?: JobKind;
  priority?: 'normal' | 'high';
  contactId?: string | null;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  title?: string;
  deviceName?: string;
  deviceSerial?: string;
  accessCode?: string;
  accessories?: string;
  condition?: string;
  problem?: string;
  diagnosis?: string;
  workDone?: string;
  internalNote?: string;
  estimatePrice?: string | null;
  approvedLimit?: string | null;
  dueAt?: string | null;
  assignedUserId?: string | null;
}

export interface JobItemInputRow {
  kind: JobItemKind;
  text: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  vatRate: number;
  productId: string | null;
}

function useJobMutation<V>(fn: (v: V) => Promise<{ job: Job }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: ({ job }) => {
      qc.setQueryData(['job', job.id], job);
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['jobs-summary'] });
    },
  });
}

export function useCreateJob() {
  return useJobMutation((input: JobFormInput & { companyId: string }) =>
    api.post<{ job: Job }>('/api/jobs', input),
  );
}

export function useUpdateJob(id: string) {
  return useJobMutation((input: JobFormInput) => api.patch<{ job: Job }>(`/api/jobs/${id}`, input));
}

export function useJobStatus(id: string) {
  return useJobMutation(
    (input: {
      status: JobStatus;
      note?: string;
      outcome?: 'repaired' | 'not_repaired';
      warrantyDays?: number;
    }) => api.post<{ job: Job }>(`/api/jobs/${id}/status`, input),
  );
}

export function useJobItems(id: string) {
  return useJobMutation((items: JobItemInputRow[]) =>
    api.put<{ job: Job }>(`/api/jobs/${id}/items`, { items }),
  );
}

export function useJobNote(id: string) {
  return useJobMutation((text: string) =>
    api.post<{ job: Job }>(`/api/jobs/${id}/notes`, { text }),
  );
}

export function useJobNotify(id: string) {
  return useJobMutation((input: { channel: 'email' | 'sms' | 'phone'; message?: string }) =>
    api.post<{ job: Job }>(`/api/jobs/${id}/notify`, input),
  );
}

/** Koncept faktury z položek zakázky; vrací id konceptu pro dokončení v Novém dokladu. */
export function useJobInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethod: 'cash' | 'card' | 'transfer') =>
      api.post<{ documentId: string; job: Job }>(`/api/jobs/${id}/invoice`, { paymentMethod }),
    onSuccess: ({ job }) => {
      qc.setQueryData(['job', job.id], job);
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useJobPrint(id: string) {
  return useQuery({
    queryKey: ['job-print', id],
    queryFn: () => api.get<JobPrint>(`/api/jobs/${id}/print`),
    enabled: !!id,
  });
}

// ---- analytika webů ----

export interface WebRange {
  companyId: string;
  /** all nebo id webu */
  site: string;
  from: string;
  to: string;
}

const webQs = (r: WebRange) =>
  `companyId=${r.companyId}&site=${encodeURIComponent(r.site)}&from=${r.from}&to=${r.to}`;

export function useWebSites(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['web-sites', companyId],
    queryFn: async () =>
      (await api.get<{ sites: WebSite[] }>(`/api/web/sites?companyId=${companyId}`)).sites,
    enabled: !!companyId,
    retry: false,
  });
}

export function useCreateWebDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      api.post<{ created: string[]; sites: WebSite[] }>('/api/web/sites/defaults', { companyId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web-sites'] }),
  });
}

export function useCreateWebSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; name: string; domain: string; color?: string }) =>
      api.post<{ site: WebSite }>('/api/web/sites', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web-sites'] }),
  });
}

export function useUpdateWebSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      color?: string;
      isActive?: boolean;
      invoiceMode?: 'off' | 'review' | 'auto';
      invoiceMargin?: boolean;
      purchaseDocs?: 'none' | 'dropship' | 'stock';
    }) => api.patch<{ site: WebSite }>(`/api/web/sites/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web-sites'] }),
  });
}

/** Faktura k zaplacené objednávce z webu – vznikne koncept k potvrzení. */
export function useInvoiceWebOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<{ state: string; documentId?: string; error?: string }>(
        `/api/web/orders/${orderId}/invoice`,
        {},
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['web-orders'] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useWebOverview(r: WebRange, enabled = true) {
  return useQuery({
    queryKey: ['web-overview', r],
    queryFn: () => api.get<WebOverview>(`/api/web/overview?${webQs(r)}`),
    enabled: enabled && !!r.companyId,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useWebDimension(r: WebRange, dim: WebDimension, enabled = true) {
  return useQuery({
    queryKey: ['web-dim', r, dim],
    queryFn: async () =>
      (await api.get<{ rows: WebDimRow[] }>(`/api/web/dimension?${webQs(r)}&dim=${dim}`)).rows,
    enabled: enabled && !!r.companyId,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useWebOrders(r: WebRange, enabled = true) {
  return useQuery({
    queryKey: ['web-orders', r],
    queryFn: () => api.get<WebOrders>(`/api/web/orders?${webQs(r)}`),
    enabled: enabled && !!r.companyId,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useWebCustomers(r: WebRange, q: string, scope: 'all' | 'range', enabled = true) {
  return useQuery({
    queryKey: ['web-customers', r, q, scope],
    queryFn: () =>
      api.get<WebCustomers>(
        `/api/web/customers?${webQs(r)}&scope=${scope}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: enabled && !!r.companyId,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Všechny nákupy a rezervace jednoho zákazníka (karta zákazníka v analytice webů). */
export function useWebCustomerOrders(companyId: string, key: string, enabled = true) {
  return useQuery({
    queryKey: ['web-customer-orders', companyId, key],
    queryFn: () =>
      api.get<WebCustomerOrders>(
        `/api/web/customers/${encodeURIComponent(key)}/orders?companyId=${encodeURIComponent(companyId)}`,
      ),
    enabled: enabled && !!companyId && !!key,
    retry: false,
  });
}

/** Kdo je na webu teď – obnovuje se každých 10 s, dokud je záložka otevřená. */
export function useWebRealtime(companyId: string, site: string, enabled = true) {
  return useQuery({
    queryKey: ['web-realtime', companyId, site],
    queryFn: () =>
      api.get<WebRealtime>(
        `/api/web/realtime?companyId=${companyId}&site=${encodeURIComponent(site)}`,
      ),
    enabled: enabled && !!companyId,
    refetchInterval: 10_000,
    retry: false,
  });
}
